import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { Navigate } from 'react-router';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import * as Yup from 'yup';
import { OrderScenario } from 'components/order/model/Order';
import { getLocalMerchantId } from 'components/settings/localStore';
import { getOnCloseRedirectUrl, getUserId } from 'components/user/localAuth';
import { TextFormField } from 'lib/form/TextFormField';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { activate, clearErrors } from 'store/auth/authSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { ROUTES } from 'pages/routes';

const PREFIX = 'ActivateForm';

const classes = {
    root: `${PREFIX}root`,
    button: `${PREFIX}button`
};

const Root = styled('div', {
    shouldForwardProp: prop => prop !== 'noPadding'
})<{ noPadding?: boolean }>(({ theme, noPadding }) => ({
    [`& .${classes.root}`]: {
        padding: noPadding ? 0 : theme.spacing(2),
        paddingTop: noPadding ? 0 : theme.spacing(4)
    },
    [`& .${classes.button}`]: {
        height: '48px',
        fontSize: '1.2em'
    }
}));

interface ActivateFormData {
    code: string;
}

const initialFields: ActivateFormData = {
    code: ''
};

export interface ActivateFormProps {
    /**
     * Called once the activation has been performed AND the resulting auth state is logged-in.
     * When provided, the default redirect (to EMAIL_CAPTURE / return URL / BASE) is suppressed.
     */
    onSuccess?: () => void;
    /** Drop the outer padding — useful when the form is embedded in a tight container. */
    noPadding?: boolean;
}

export const ActivateForm: React.FC<ActivateFormProps> = ({ onSuccess, noPadding = false }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const { getParsedPath, push } = useLocalHistory();
    const { id, user, activateError } = useSelector((state: ApplicationState) => state.auth);
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { locationId } = useSelector((state: ApplicationState) => state.basket);
    const [scenario] = useRouteScenario();
    const { isGuest, isLoggedIn } = useAuth();
    const signinValidation = React.useMemo(
        () =>
            Yup.object().shape({
                code: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
            }),
        [t]
    );
    const userId = React.useMemo(() => id || getUserId(), [id]);
    const handleSubmit = React.useCallback(
        (data: ActivateFormData) => dispatch(activate({ id: userId, token: String(data.code) })),
        [userId, dispatch]
    );
    const shouldEnterEmail = React.useMemo(
        () => !!settings && settings.emailAuthEnabled && settings.phoneAuthEnabled,
        [settings]
    );
    React.useEffect(() => {
        if (isLoggedIn && !isGuest) {
            // Inline consumer asked to be told instead of redirected.
            if (onSuccess) {
                onSuccess();
                return;
            }
            const merchantId = getLocalMerchantId();
            if (merchantId) {
                if (shouldEnterEmail) {
                    push(getParsedPath(ROUTES.USER.EMAIL_CAPTURE, { merchantId }));
                } else {
                    const returnUrl = getOnCloseRedirectUrl(merchantId);
                    if (returnUrl) {
                        const [path, search] = returnUrl.split('?');
                        push(path, {}, search);
                    } else if (scenario === OrderScenario.TABLE) {
                        push(ROUTES.JOURNEY.LANDING, { merchantId, locationId });
                    } else {
                        push(ROUTES.BASE);
                    }
                }
            }
        }
    }, [user, push, isGuest, isLoggedIn, getParsedPath, scenario, locationId, shouldEnterEmail, onSuccess]);
    React.useEffect(
        () => () => {
            dispatch(clearErrors());
        },
        [dispatch]
    );

    if (!userId) {
        // When rendered as a page this Navigate sends them to /login. Inline this still bails
        // out gracefully — the receipt component should only mount this once register has run.
        return <Navigate to={getParsedPath(ROUTES.USER.LOGIN)} />;
    }

    return (
        <Root noPadding={noPadding}>
            <Formik initialValues={initialFields} validationSchema={signinValidation} onSubmit={handleSubmit}>
                {({ submitForm, isSubmitting, isValid, values: { code } }) => (
                    <Form className={classes.root}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography>{t('ONBOARDING_ACTIVATION_MESSAGE')}</Typography>
                            </Grid>
                            <Grid size={12}>
                                <TextFormField
                                    name="code"
                                    type="number"
                                    label={t('ONBOARDING_ACTIVATION_CODE')}
                                />
                            </Grid>
                            {!!activateError && (
                                <Grid size={12}>
                                    <Typography color="error">{activateError}</Typography>
                                </Grid>
                            )}
                            <Grid size={12}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    disabled={!isValid || !code || isSubmitting}
                                    fullWidth
                                    onClick={submitForm}
                                    className={classes.button}
                                    loading={isSubmitting}
                                >
                                    {t('BUTTON_SUBMIT')}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Root>
    );
};
